import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import useClientService from '../../hooks/useClientService';
import { useSnackbar } from '../../components/SnackbarProvider';
import { handleApiError } from '../../components/Utils';
import {
    Button,
    TextField,
    Grid,
    Box,
    Paper,
    Typography,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@mui/material";

function NewClient() {
    const { createClient } = useClientService();
    const showSnackbar = useSnackbar();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const [clientForm, setClientForm] = useState({
        email: null,
        name: null,
        address: null,
        doc_type: null,
        documentation: null,
        country: null,
        cp: null,
        town: null,
        commercial_name: null,
        province: null,
        telephone_number: null,
        secondary_telephone_number: null
    });

    const handleChange = (event) => {
        const { name, value } = event.target;
        setClientForm(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const validateForm = () => {
        if (!clientForm.name) {
            return "Por favor, añada un nombre";
        }
        if (clientForm.email && clientForm.email.length > 36) {
            return "El email no debe exceder los 36 caracteres";
        }
        if (clientForm.name.length > 55) {
            return "El nombre no debe exceder los 55 caracteres";
        }
        if (clientForm.country && clientForm.country.length > 36) {
            return "El pais no debe exceder los 36 caracteres";
        }
        if (clientForm.cp && clientForm.cp.length > 9) {
            return "El codigo postal no debe exceder los 9 caracteres";
        }
        if (clientForm.town && clientForm.town.length > 150) {
            return "La comunidad autonoma no debe exceder los 150 caracteres";
        }
        if (clientForm.province && clientForm.province.length > 150) {
            return "La provincia no debe exceder los 150 caracteres";
        }
        if (clientForm.surname && clientForm.surname.length > 36) {
            return "Los apellidos no deben exceder los 36 caracteres";
        }
        if ((clientForm.doc_type && !clientForm.documentation) || (!clientForm.doc_type && clientForm.documentation)) {
            return "Si quiere poner una documentación, debe rellenar ambos campos (tipo y documentación).";
        }
        if (clientForm.documentation && clientForm.documentation.length > 15) {
            return "La documentación no debe exceder los 15 caracteres.";
        }
        if (clientForm.address && clientForm.address.length > 150) {
            return "La dirección no debe exceder los 150 caracteres";
        }
        if (clientForm.telephone_number && clientForm.telephone_number.length > 15) {
            return "El teléfono no debe exceder los 15 caracteres";
        }
        if (clientForm.secondary_telephone_number && clientForm.secondary_telephone_number.length > 15) {
            return "El teléfono secundario no debe exceder los 15 caracteres";
        }
        if (clientForm.commercial_name && clientForm.commercial_name.length > 36) {
            return "El alias no debe exceder los 36 caracteres";
        }
        return null;
    };

    const handleCreateClient = async (event) => {
        event.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }
        try {
            setLoading(true);

            await createClient(clientForm);

            showSnackbar("Cliente creado correctamente", "success");
            navigate(`/clients/list`);
            setError(null);
        } catch (error) {
            const msg = handleApiError(error, 'Error al crear el cliente.');
            setError(msg);
            showSnackbar(msg, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '120%' }}>
            <Paper elevation={3} style={{ padding: 20, width: '80%' }}>
                <Typography component="h2" variant="h5">
                    Nuevo Cliente
                </Typography>
                <br />
                <form onSubmit={handleCreateClient}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} style={{ marginTop: "20px" }}>
                            <Typography variant="h6">Información Principal</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Nombre"
                                name="name"
                                value={clientForm.name}
                                onChange={handleChange}
                                inputProps={{ maxLength: 55 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                value={clientForm.email}
                                onChange={handleChange}
                                inputProps={{ maxLength: 36 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Teléfono"
                                name="telephone_number"
                                value={clientForm.telephone_number}
                                onChange={handleChange}
                                inputProps={{ maxLength: 15 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Teléfono secundario"
                                name="secondary_telephone_number"
                                value={clientForm.secondary_telephone_number}
                                onChange={handleChange}
                                inputProps={{ maxLength: 15 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Nombre comercial (alias)"
                                name="commercial_name"
                                value={clientForm.commercial_name}
                                onChange={handleChange}
                                inputProps={{ maxLength: 36 }}
                            />
                        </Grid>
                        <Grid item xs={12} style={{ marginTop: "20px" }}>
                            <Typography variant="h6">Documentación</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl
                                style={{ width: "100%" }}
                            >
                                <InputLabel id="document-type-label">
                                    Documentación
                                </InputLabel>
                                <Select
                                    labelId="document-type-label"
                                    id="document-type"
                                    label='Tipo documentación'
                                    value={clientForm.doc_type}
                                    onChange={handleChange}
                                    name="doc_type"
                                    inputProps={{ id: 'document-type' }}
                                >
                                    <MenuItem value="DNI">DNI</MenuItem>
                                    <MenuItem value="NIF">NIF</MenuItem>
                                    <MenuItem value="NIE">NIE</MenuItem>
                                    <MenuItem value="CIF">CIF</MenuItem>
                                    <MenuItem value="DOC_EXTRANJERA">DOC EXTRANJERA</MenuItem>
                                    <MenuItem value="OTRO">OTRO</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Documentación"
                                name="documentation"
                                value={clientForm.documentation}
                                onChange={handleChange}
                                inputProps={{ maxLength: 15 }}
                            />
                        </Grid>
                        <Grid item xs={12} style={{ marginTop: "20px" }}>
                            <Typography variant="h6">Dirección</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="País"
                                name="country"
                                value={clientForm.country}
                                onChange={handleChange}
                                inputProps={{ maxLength: 36 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Población"
                                name="town"
                                value={clientForm.town}
                                onChange={handleChange}
                                inputProps={{ maxLength: 150 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Provincia"
                                name="province"
                                value={clientForm.province}
                                onChange={handleChange}
                                inputProps={{ maxLength: 150 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Código postal"
                                name="cp"
                                value={clientForm.cp}
                                onChange={handleChange}
                                inputProps={{ maxLength: 9 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextField
                                fullWidth
                                label="Dirección"
                                name="address"
                                value={clientForm.address}
                                onChange={handleChange}
                                inputProps={{ maxLength: 150 }}
                            />
                        </Grid>
                    </Grid>
                    <br />
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end">
                            <Button type="submit" variant="contained" color="primary" disabled={loading}>
                                {loading ? <CircularProgress size={24} /> : 'Crear'}
                            </Button>
                        </Box>
                    </Grid>
                </form>
                {error && (
                    <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                        {error}
                    </Typography>
                )}
            </Paper >
        </div >
    );
}

export default NewClient;
